import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { disposeScene } from '../utils/ThreeCleanup';
import SoundManager from '../utils/SoundManager';

const YOKAI_CHARS = [
  '鬼', '狐', '河童', '天狗', '雪女', '龍', '鵺', '妖怪',
  '化', '物', '幽', '魂', '霊', '怪', '夜', '闇',
  '鴉', '蛇', '猫又', '提灯', '骸', '影', '魔',
];

const HyakkiVisualization: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!containerRef.current) return;

    SoundManager.playAmbient();

    const container = containerRef.current;
    const width = container.clientWidth;
    const height = container.clientHeight;

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x000000);
    scene.fog = new THREE.Fog(0x000000, 4, 18);

    const camera = new THREE.PerspectiveCamera(70, width / height, 0.1, 100);
    camera.position.z = 8;
    camera.position.y = 0;

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setSize(width, height);
    container.appendChild(renderer.domElement);

    // Moon (a circle sprite top-right)
    const moonCanvas = document.createElement('canvas');
    moonCanvas.width = 256;
    moonCanvas.height = 256;
    const moonCtx = moonCanvas.getContext('2d')!;
    const grad = moonCtx.createRadialGradient(128, 128, 30, 128, 128, 120);
    grad.addColorStop(0, 'rgba(255, 245, 200, 1)');
    grad.addColorStop(0.6, 'rgba(255, 220, 150, 0.4)');
    grad.addColorStop(1, 'rgba(255, 200, 100, 0)');
    moonCtx.fillStyle = grad;
    moonCtx.fillRect(0, 0, 256, 256);
    const moonTexture = new THREE.CanvasTexture(moonCanvas);
    const moonMaterial = new THREE.SpriteMaterial({ map: moonTexture, transparent: true });
    const moon = new THREE.Sprite(moonMaterial);
    moon.scale.set(4, 4, 1);
    moon.position.set(6, 4, -5);
    scene.add(moon);

    // Stars
    const starGeo = new THREE.BufferGeometry();
    const starCount = 200;
    const starPositions = new Float32Array(starCount * 3);
    for (let i = 0; i < starCount; i++) {
      starPositions[i * 3] = (Math.random() - 0.5) * 40;
      starPositions[i * 3 + 1] = (Math.random() - 0.5) * 20;
      starPositions[i * 3 + 2] = -10 - Math.random() * 5;
    }
    starGeo.setAttribute('position', new THREE.BufferAttribute(starPositions, 3));
    const starMaterial = new THREE.PointsMaterial({
      color: 0xaaaa66,
      size: 0.04,
      transparent: true,
      opacity: 0.6,
    });
    const stars = new THREE.Points(starGeo, starMaterial);
    scene.add(stars);

    // Build a yokai sprite from a kanji string
    const makeYokaiSprite = (char: string, hue: number): THREE.Sprite => {
      const canvas = document.createElement('canvas');
      canvas.width = 256;
      canvas.height = 256;
      const ctx = canvas.getContext('2d')!;
      ctx.fillStyle = 'rgba(0,0,0,0)';
      ctx.fillRect(0, 0, 256, 256);

      // Glow halo
      const halo = ctx.createRadialGradient(128, 128, 20, 128, 128, 120);
      halo.addColorStop(0, `hsla(${hue}, 80%, 70%, 0.5)`);
      halo.addColorStop(0.5, `hsla(${hue}, 80%, 50%, 0.15)`);
      halo.addColorStop(1, `hsla(${hue}, 80%, 50%, 0)`);
      ctx.fillStyle = halo;
      ctx.fillRect(0, 0, 256, 256);

      // Character
      const fontSize = char.length > 1 ? 110 : 160;
      ctx.font = `bold ${fontSize}px 'Yu Gothic', 'Hiragino Sans', 'Noto Sans JP', sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.shadowColor = `hsl(${hue}, 90%, 60%)`;
      ctx.shadowBlur = 25;
      ctx.fillStyle = `hsl(${hue}, 90%, 75%)`;
      ctx.fillText(char, 128, 128);

      const texture = new THREE.CanvasTexture(canvas);
      texture.needsUpdate = true;
      const mat = new THREE.SpriteMaterial({ map: texture, transparent: true });
      return new THREE.Sprite(mat);
    };

    // Procession: array of yokai sprites with individual motion
    type Yokai = {
      sprite: THREE.Sprite;
      speed: number;
      bobAmp: number;
      bobFreq: number;
      bobPhase: number;
      baseY: number;
    };
    const procession: Yokai[] = [];
    const SPAWN_X = 14;
    const DESPAWN_X = -14;

    const spawnYokai = (xOverride?: number) => {
      const char = YOKAI_CHARS[Math.floor(Math.random() * YOKAI_CHARS.length)];
      // Hue palette: greenish, ghost-blue, pale violet, ember
      const huePool = [120, 160, 200, 270, 30];
      const hue = huePool[Math.floor(Math.random() * huePool.length)];
      const sprite = makeYokaiSprite(char, hue);
      const depth = -2 + Math.random() * 4; // z
      const scale = 0.8 + (depth + 2) * 0.2; // closer = bigger
      sprite.scale.set(scale, scale, 1);
      const baseY = -1.5 + Math.random() * 3;
      sprite.position.set(xOverride ?? SPAWN_X, baseY, depth);
      scene.add(sprite);
      procession.push({
        sprite,
        speed: 0.012 + Math.random() * 0.025,
        bobAmp: 0.1 + Math.random() * 0.3,
        bobFreq: 0.5 + Math.random() * 1.5,
        bobPhase: Math.random() * Math.PI * 2,
        baseY,
      });
    };

    // Pre-populate the scene so it looks alive immediately
    for (let i = 0; i < 12; i++) {
      spawnYokai(-10 + i * 2.5);
    }

    let lastSpawn = performance.now();
    const SPAWN_INTERVAL = 900; // ms

    // Resize
    const onResize = () => {
      if (!container) return;
      const w = container.clientWidth;
      const h = container.clientHeight;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
    };
    window.addEventListener('resize', onResize);

    let frameId = 0;
    const startTs = performance.now();
    const animate = () => {
      frameId = requestAnimationFrame(animate);
      const t = (performance.now() - startTs) / 1000;

      // Twinkle stars
      starMaterial.opacity = 0.45 + Math.sin(t * 0.7) * 0.15;

      // Slow moon glow pulse
      moon.material.opacity = 0.85 + Math.sin(t * 0.3) * 0.15;

      // Update procession
      for (let i = procession.length - 1; i >= 0; i--) {
        const y = procession[i];
        y.sprite.position.x -= y.speed;
        y.sprite.position.y = y.baseY + Math.sin(t * y.bobFreq + y.bobPhase) * y.bobAmp;
        if (y.sprite.position.x < DESPAWN_X) {
          scene.remove(y.sprite);
          y.sprite.material.map?.dispose();
          y.sprite.material.dispose();
          procession.splice(i, 1);
        }
      }

      // Spawn new yokai
      if (performance.now() - lastSpawn > SPAWN_INTERVAL) {
        spawnYokai();
        lastSpawn = performance.now();
      }

      renderer.render(scene, camera);
    };
    animate();

    return () => {
      cancelAnimationFrame(frameId);
      window.removeEventListener('resize', onResize);
      disposeScene(scene, renderer);
      if (container.contains(renderer.domElement)) {
        container.removeChild(renderer.domElement);
      }
    };
  }, []);

  return (
    <div className="relative w-full h-full">
      <div ref={containerRef} className="w-full h-full" />
      <div className="absolute top-2 left-3 text-[#7aff94] pointer-events-none">
        <div
          className="text-lg font-bold"
          style={{ fontFamily: "'Yu Gothic', 'Hiragino Sans', 'Noto Sans JP', sans-serif", textShadow: '0 0 10px rgba(95,233,121,0.8)' }}
        >
          百鬼夜行
        </div>
        <div className="text-xs opacity-70">HYAKKI YAGYŌ — Night Parade of 100 Demons</div>
      </div>
    </div>
  );
};

export default HyakkiVisualization;
